// src/pages/FaqPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ArrowLeft, HelpCircle } from "lucide-react";

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Perguntas Frequentes`;

// Subcomponente do Accordion (Gaveta)
const FaqItem = ({ question, answer, btnText, btnLink, isOpen, onClick }) => {
  const navigate = useNavigate();

  return (
    <div
      className={`bg-white/60 backdrop-blur-sm border rounded-2xl overflow-hidden shadow-sm transition-all duration-300 ${isOpen ? "border-[#971253]/30 shadow-md" : "border-[#dbcdac]/40 hover:border-[#528a89]/50"}`}
    >
      <button
        onClick={onClick}
        className="w-full px-6 py-5 text-left flex justify-between items-center gap-4 focus:outline-none"
      >
        <h3
          className={`font-serif text-lg md:text-xl transition-colors duration-300 ${isOpen ? "text-[#971253]" : "text-stone-700"}`}
        >
          {question}
        </h3>
        <div
          className={`p-1.5 rounded-full bg-[#dbcdac]/20 text-[#528a89] transition-transform duration-300 shrink-0 ${isOpen ? "rotate-180 bg-[#971253]/10 text-[#971253]" : ""}`}
        >
          <ChevronDown size={20} />
        </div>
      </button>
      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-6 text-stone-600 font-light leading-relaxed">
            {answer}
            <br></br>
            {btnText && btnLink && (
              <button
                onClick={() => navigate(btnLink)}
                className="mt-5 px-6 py-2.5 bg-[#528a89] text-[#FFF5EE] text-sm font-bold uppercase tracking-widest rounded-xl hover:bg-[#3a6362] transition-colors shadow-sm inline-block"
              >
                {btnText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function FaqPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      q: "Posso jogar mesmo sem conhecer as regras?",
      a: "Sim! Mesmo quem nunca jogou antes é bem-vindo. Nossa equipe explica as regras e ajuda você a dar os primeiros passos na partida.",
    },
    {
      q: "Posso ir sozinho ou preciso vir em grupo?",
      a: "Sim, você pode vir sozinho sem problemas. Para quem quer conhecer mais jogadores, a assinatura é uma ótima opção: os assinantes fazem parte de um grupo da comunidade e frequentemente trazem seus próprios jogos para jogar na Deli da Persy, criando novas mesas e oportunidades para jogar com outras pessoas.",
      btnText: "Conheça o Clube",
      btnLink: "/sobre/assinatura",
    },
    {
      q: "Posso levar ou pedir comida de fora?",
      a: "Não.",
    },
    {
      q: "Posso ir conhecer?",
      a: "Claro, você pode vir conhecer. Apenas lembramos que, devido ao espaço limitado, o passaporte é necessário para quem deseja sentar em uma de nossas mesas. Se for apenas uma visita rápida para adquirir um de nossos produtos, fique à vontade.",
    },
    {
      q: "Como saber se vocês têm um determinado jogo?",
      a: "Você pode verificar nosso catálogo de jogos. Ele mostra os títulos disponíveis atualmente em nosso acervo. Basta clicar no botão abaixo para ver todos os títulos disponíveis na Deli da Persy.",
      btnText: "Ver Catálogo Completo",
      btnLink: "/biblioteca/acervo",
    },
    {
      q: "Reserva é necessária?",
      a: (
        <>
          <p className="mb-3">
            Não é obrigatório fazer reserva, mas recomendamos sempre que
            possível. Reservando antecipadamente você aproveita algumas
            oportunidades:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-stone-600">
            <li>
              <strong className="font-medium text-stone-700">
                Garantia de mesa:
              </strong>{" "}
              seu lugar fica reservado para você aproveitar com tranquilidade.
            </li>
            <li>
              <strong className="font-medium text-stone-700">
                Desconto no passaporte:
              </strong>{" "}
              quem reserva antecipadamente paga menos.
            </li>
            <li>
              <strong className="font-medium text-stone-700">
                Mais comodidade:
              </strong>{" "}
              você evita vir até o café e não encontrar lugares disponíveis.
            </li>
          </ul>
        </>
      ),
      btnText: "Fazer Reserva",
      btnLink: "/sobre/reserva",
    },
    {
      q: "Vocês alugam jogos?",
      a: "Não trabalhamos com aluguel de jogos.",
    },
    {
      q: "Posso jogar RPG no café? Como funciona?",
      a: "Sim, você pode jogar RPG na Deli da Persy.  Clique no botão abaixo para acessar a página onde explicamos como funciona a participação e organização das mesas.",
      btnText: "Saiba mais sobre RPG",
      btnLink: "/sobre/rpg",
    },
    {
      q: "Quero ser assinante. Como faço?",
      a: "Você pode se tornar assinante em poucos passos. Clique no botão abaixo para conhecer nossos planos de assinatura e escolher a opção que melhor combina com você.",
      btnText: "Conheça o Clube",
      btnLink: "/sobre/assinatura",
    },
    {
      q: "Como faço para fazer minha festa na Deli da Persy?",
      a: "Você pode organizar sua festa através do nosso formulário de reserva, usando o nosso pacote focado para aniversários.",
      btnText: "Fazer Reserva",
      btnLink: "/sobre/reserva",
    },
    {
      q: "Como funciona o passaporte?",
      a: "O passaporte no valor de R$ 35 permite jogar nosso acervo a noite inteira. Ao reservar antecipadamente, você recebe desconto e paga apenas R$ 29.",
    },
    {
      q: "Qual horário de funcionamento?",
      a: "Confira nossa localização e horários de funcionamento atualizados.",
      btnText: "Ver Localização",
      btnLink: "/local",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
      <title>{TITLE}</title>
      <link rel="icon" href={ICON} />

      {/* Textura Vintage de Fundo */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

      <div className="max-w-3xl mx-auto px-4 md:px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Botão de Voltar */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-400 hover:text-[#971253] font-bold uppercase tracking-widest text-xs mb-8 transition-colors group"
        >
          <ArrowLeft
            size={16}
            className="group-hover:-translate-x-1 transition-transform"
          />
          Voltar
        </button>

        {/* Cabeçalho */}
        <div className="text-center md:text-left mb-12 flex flex-col md:flex-row items-center md:items-start gap-4">
          <div className="w-16 h-16 bg-[#528a89]/10 rounded-full flex items-center justify-center shrink-0">
            <HelpCircle size={32} className="text-[#528a89]" />
          </div>
          <div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#971253] mb-3">
              Perguntas Frequentes
            </h1>
            <p className="text-lg text-[#528a89] font-bold text-start">
              Tem alguma dúvida ?
            </p>
            <p className="text-lg text-[#528a89] font-light text-justify">
              Reunimos aqui respostas para as perguntas mais comuns sobre o
              funcionamento da Deli da Persy, escolha de jogos e experiência no
              espaço. Explore as perguntas abaixo para encontrar a informação
              que precisa. E, se algo ainda não estiver claro, estamos sempre
              por perto para ajudar.
            </p>
          </div>
        </div>

        {/* Lista de FAQs */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              question={faq.q}
              answer={faq.a}
              btnText={faq.btnText}
              btnLink={faq.btnLink}
              isOpen={openIndex === index}
              onClick={() => toggleFaq(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FaqPage;
